import { usePricingQuery } from 'api'

// programme list prices, which are not in the checkout config
// the Jumpstart MSRP is the ad page's "MSRP $344.95" ($244.95 before 12 October 2026); the sterling figure follows
// the £29.95 / $34.95 price ratio
const LIST_PRICES = {
  USD: { currency: 'USD', symbol: '$', accelerate: 695, fullCertification: 995, jumpstartMsrp: 344.95 },
  GBP: { currency: 'GBP', symbol: '£', accelerate: 595, fullCertification: 895, jumpstartMsrp: 295.95 },
}

const JUMPSTART_DAYS = 30

const PRICE_TOKEN = /\{price\}/g

export const formatAmount = (symbol, amount) =>
  `${symbol}${amount.toLocaleString('en-GB', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`

// the Jumpstart price in major units, e.g. 29.95; null until the visitor's pricing loads
export const jumpstartAmount = ({ products } = {}) => {
  const price = products?.jumpstartProgram?.price
  return Number.isFinite(price) ? price / 100 : null
}

// the Jumpstart price as checkout will charge it, e.g. '£29.95'; null until the visitor's pricing loads
export const jumpstartPrice = (pricing = {}) => {
  const amount = jumpstartAmount(pricing)
  return pricing.currencySymbol && amount !== null ? formatAmount(pricing.currencySymbol, amount) : null
}

// the price spread over the 30 days, e.g. '$1.17'; the ad page's "about $1.17 a day"
export const jumpstartPerDay = (pricing = {}) => {
  const amount = jumpstartAmount(pricing)
  return pricing.currencySymbol && amount !== null
    ? formatAmount(pricing.currencySymbol, Math.round((amount / JUMPSTART_DAYS) * 100) / 100)
    : null
}

export const listPrices = (currency) => {
  const list = LIST_PRICES[currency] || LIST_PRICES.USD
  return {
    symbol: list.symbol,
    accelerate: formatAmount(list.symbol, list.accelerate),
    fullCertification: formatAmount(list.symbol, list.fullCertification),
    jumpstartMsrp: list.jumpstartMsrp ? formatAmount(list.symbol, list.jumpstartMsrp) : null,
    // a value stack item carries an amount per currency, e.g. { USD: 150, GBP: 115 }
    value: (amounts) => `${formatAmount(list.symbol, amounts[list.currency])} value`,
    // the stack's total from its items so it cannot drift; items without a value (the fast track) add nothing
    totalOf: (items) =>
      formatAmount(
        list.symbol,
        items.reduce((sum, item) => sum + (item.value?.[list.currency] || 0), 0),
      ),
  }
}

// 'Start Your Jumpstart' → 'Start Your Jumpstart, £29.95' once the price is known
export const withPrice = (label, price, separator = ', ') => (price ? `${label}${separator}${price}` : label)

// fills {price} in copy; null while the price is unknown, so the caller can leave that line out
export const fillPrice = (text, price) => {
  if (!text.match(PRICE_TOKEN)) return text
  return price ? text.replace(PRICE_TOKEN, price) : null
}

// fills {price} in every string field; an item that needs the price is left out until it loads
export const pricedItems = (items, price) =>
  items
    .map((item) =>
      Object.fromEntries(
        Object.entries(item).map(([key, value]) => [key, typeof value === 'string' ? fillPrice(value, price) : value]),
      ),
    )
    .filter((item) => Object.values(item).every((value) => value !== null))

export const useVisitorPrices = () => {
  const { data } = usePricingQuery({ initialDataUpdatedAt: 0, staleTime: 10 * 60 * 1000 })
  return {
    // 'GBP' or 'USD'; null until the visitor's pricing loads
    currency: data?.currency || null,
    jumpstart: jumpstartPrice(data),
    jumpstartAmount: jumpstartAmount(data),
    jumpstartPerDay: jumpstartPerDay(data),
    ...listPrices(data?.currency),
  }
}
